import React, { forwardRef, useRef, useState, useEffect } from 'react';
import { getDefaultZIndex, useMantineDefaultProps } from '@mantine/styles';
import { useMergedRef } from '@mantine/hooks';
import useStyles from './Tooltip.styles.js';
import { Box } from '../Box/Box.js';
import { Popper } from '../Popper/Popper.js';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const defaultProps = {
  openDelay: 0,
  closeDelay: 0,
  gutter: 5,
  color: "gray",
  disabled: false,
  withArrow: false,
  arrowSize: 2,
  position: "top",
  placement: "center",
  transition: "pop-top-left",
  transitionDuration: 100,
  zIndex: getDefaultZIndex("popover"),
  width: "auto",
  wrapLines: false,
  allowPointerEvents: false,
  positionDependencies: [],
  withinPortal: true
};
const Tooltip = forwardRef((props, ref) => {
  const _a = useMantineDefaultProps("Tooltip", defaultProps, props), {
    className,
    label,
    children,
    opened,
    openDelay,
    closeDelay,
    gutter,
    color,
    radius,
    disabled,
    withArrow,
    arrowSize,
    position,
    placement,
    transition,
    transitionDuration,
    zIndex,
    transitionTimingFunction,
    width,
    wrapLines,
    allowPointerEvents,
    positionDependencies,
    withinPortal,
    tooltipRef,
    tooltipId,
    classNames,
    styles,
    onMouseLeave,
    onMouseEnter
  } = _a, others = __objRest(_a, [
    "className",
    "label",
    "children",
    "opened",
    "openDelay",
    "closeDelay",
    "gutter",
    "color",
    "radius",
    "disabled",
    "withArrow",
    "arrowSize",
    "position",
    "placement",
    "transition",
    "transitionDuration",
    "zIndex",
    "transitionTimingFunction",
    "width",
    "wrapLines",
    "allowPointerEvents",
    "positionDependencies",
    "withinPortal",
    "tooltipRef",
    "tooltipId",
    "classNames",
    "styles",
    "onMouseLeave",
    "onMouseEnter"
  ]);
  const { classes, cx, theme } = useStyles({ color, radius }, { classNames, styles, name: "Tooltip" });
  const openTimeoutRef = useRef();
  const closeTimeoutRef = useRef();
  const [_opened, setOpened] = useState(false);
  const visible = (typeof opened === "boolean" ? opened : _opened) && !disabled;
  const [referenceElement, setReferenceElement] = useState(null);
  const mergedRefs = useMergedRef(ref, setReferenceElement);
  const handleOpen = () => {
    window.clearTimeout(closeTimeoutRef.current);
    if (openDelay !== 0) {
      openTimeoutRef.current = window.setTimeout(() => {
        setOpened(true);
      }, openDelay);
    } else {
      setOpened(true);
    }
  };
  const handleClose = () => {
    window.clearTimeout(openTimeoutRef.current);
    if (closeDelay !== 0) {
      closeTimeoutRef.current = window.setTimeout(() => {
        setOpened(false);
      }, closeDelay);
    } else {
      setOpened(false);
    }
  };
  useEffect(() => () => {
    window.clearTimeout(openTimeoutRef.current);
    window.clearTimeout(closeTimeoutRef.current);
  }, []);
  return /* @__PURE__ */ React.createElement(Box, __spreadValues({
    className: cx(classes.root, className),
    onPointerEnter: (event) => {
      handleOpen();
      typeof onMouseEnter === "function" && onMouseEnter(event);
    },
    onPointerLeave: (event) => {
      handleClose();
      typeof onMouseLeave === "function" && onMouseLeave(event);
    },
    onFocusCapture: handleOpen,
    onBlurCapture: handleClose,
    ref: mergedRefs
  }, others), /* @__PURE__ */ React.createElement(Popper, {
    referenceElement,
    transitionDuration,
    transition,
    mounted: visible,
    position,
    placement,
    gutter,
    withArrow,
    arrowSize,
    arrowDistance: theme.fn.radius(radius) > 10 ? 7 : 3,
    zIndex,
    arrowClassName: classes.arrow,
    forceUpdateDependencies: [color, radius, ...positionDependencies],
    withinPortal
  }, /* @__PURE__ */ React.createElement(Box, {
    className: classes.body,
    ref: tooltipRef,
    sx: {
      pointerEvents: allowPointerEvents ? "all" : "none",
      whiteSpace: wrapLines ? "normal" : "nowrap",
      width
    }
  }, label)), children);
});
Tooltip.displayName = "@mantine/core/Tooltip";

export { Tooltip };
//# sourceMappingURL=Tooltip.js.map
